import type { BindingHealthRow } from '../services/monitoring';

export type IntegrationBindingLifecycle =
  | 'pending'
  | 'inactive'
  | 'healthy'
  | 'idle'
  | 'degraded'
  | 'error'
  | 'unknown';

/** Aligns with integration_monitoring_service alert heuristics (non-success, slow). */
const NON_SUCCESS_ALERT = 0.1;
const SLOW_MS = 5000;

export function deriveIntegrationBindingLifecycle(input: {
  bindingIsActive: boolean;
  bindingStatus?: string | null;
  health: BindingHealthRow | null | undefined;
  healthFetchFailed: boolean;
}): IntegrationBindingLifecycle {
  const { bindingIsActive, bindingStatus, health, healthFetchFailed } = input;

  if (bindingStatus === 'pending') {
    return 'pending';
  }
  if (!bindingIsActive || bindingStatus === 'inactive') {
    return 'inactive';
  }
  if (healthFetchFailed) {
    return 'unknown';
  }
  if (!health) {
    return 'unknown';
  }

  const ns = health.non_success_rate ?? health.failure_rate ?? 0;
  const ser = health.server_error_rate ?? 0;
  const n = health.total_events ?? 0;

  if (health.status === 'unhealthy') {
    return 'error';
  }
  if (n >= 5 && ns > 0.5) {
    return 'error';
  }
  if (n >= 3 && ser > 0.2) {
    return 'error';
  }

  if (health.status === 'degraded') {
    return 'degraded';
  }
  if (n > 0 && ns > NON_SUCCESS_ALERT) {
    return 'degraded';
  }
  if (n > 0 && health.avg_response_time > SLOW_MS) {
    return 'degraded';
  }

  if (n === 0) {
    return 'idle';
  }

  return 'healthy';
}

export function integrationBindingNeedsAttention(
  life: IntegrationBindingLifecycle
): boolean {
  return ['error', 'degraded', 'idle', 'unknown', 'pending'].includes(life);
}
